/**
 * Combat Mechanics Types for Deterministic Engine
 *
 * These TypeScript types mirror the Rust types in:
 * backend/shared/src/types/combat_mechanics.rs (canonical source)
 * backend/game-server/src/engine/deterministic_combat/types.rs (re-exports + engine-specific)
 *
 * Used for:
 * 1. Elasticsearch index storage (mechanics field)
 * 2. Character permanent data (spells/weapons/abilities with full mechanics)
 * 3. AI controller output (DeterministicIntent generation)
 *
 * Design Pattern:
 * - Computed fields (dc, attack_bonus, etc.) are optional (null in index, filled at intent time)
 * - Engine is the spec - these types match combat_mechanics.rs exactly
 */

// ============================================================================
// CORE ENUMS (mirror backend/shared/src/types/dnd.rs)
// ============================================================================

export type Ability =
  | 'strength'
  | 'dexterity'
  | 'constitution'
  | 'intelligence'
  | 'wisdom'
  | 'charisma';

export type DamageType =
  | 'acid'
  | 'bludgeoning'
  | 'cold'
  | 'fire'
  | 'force'
  | 'lightning'
  | 'necrotic'
  | 'piercing'
  | 'poison'
  | 'psychic'
  | 'radiant'
  | 'slashing'
  | 'thunder';

export type SpellSchool =
  | 'abjuration'
  | 'conjuration'
  | 'divination'
  | 'enchantment'
  | 'evocation'
  | 'illusion'
  | 'necromancy'
  | 'transmutation';

/**
 * D&D 5e Conditions (2024 rules)
 * Matches Rust Condition enum with helper methods
 */
export type Condition =
  | 'blinded'
  | 'burning' // System condition (not SRD 5.2): Searing Smite recurring fire DoT, CON save ends
  | 'charmed'
  | 'deafened'
  | 'exhaustion'
  | 'frightened'
  | 'grappled'
  | 'incapacitated'
  | 'invisible'
  | 'paralyzed'
  | 'petrified'
  | 'poisoned'
  | 'prone'
  | 'restrained'
  | 'stunned'
  | 'unconscious';

/**
 * Cover types for tactical positioning
 */
export type CoverType = 'none' | 'half' | 'three_quarters' | 'full';

/**
 * Reaction types for standing orders
 */
export type ReactionType =
  // Spells (SRD 5.2)
  | 'shield'
  | 'counterspell'
  | 'hellish_rebuke'
  | 'featherfall'
  // Combat
  | 'opportunity_attack'
  // Class Features (SRD 5.2)
  | 'deflect' // Monk
  | 'uncanny_dodge' // Rogue
  // Other
  | 'custom';

// ============================================================================
// DICE & BASIC STRUCTURES
// ============================================================================

/**
 * Dice formula for damage/healing rolls
 * Example: { count: 2, sides: 6, modifier: 3 } = "2d6+3"
 */
export interface DiceFormula {
  count: number;
  sides: number;
  modifier: number;
}

/**
 * Position on battle map (grid coordinates)
 */
export interface Position {
  x: number;
  y: number;
  z: number;
}

/**
 * Weapon range for ranged/thrown weapons
 */
export interface WeaponRange {
  normal: number;
  long: number;
}

// ============================================================================
// WEAPON TYPES
// ============================================================================

export type WeaponProperty =
  | 'ammunition'
  | 'finesse'
  | 'heavy'
  | 'light'
  | 'loading'
  | 'reach'
  | 'thrown'
  | 'two_handed'
  | 'versatile';

export type WeaponMastery =
  | 'cleave'
  | 'graze'
  | 'nick'
  | 'push'
  | 'sap'
  | 'slow'
  | 'topple'
  | 'vex';

export type EffectTrigger = 'on_hit' | 'on_crit' | 'on_kill' | 'activated';

export type EffectType = 'extra_damage' | 'condition' | 'special';

/**
 * Bonus damage (e.g., Flametongue +2d6 fire)
 */
export interface BonusDamage {
  dice: DiceFormula;
  damage_type: DamageType;
  condition?: string;
}

/**
 * Special weapon effects (e.g., vorpal sword, giant slayer)
 */
export interface WeaponSpecialEffect {
  name: string;
  trigger: EffectTrigger;
  effect_type: EffectType;
  description: string;
}

/**
 * Complete weapon data for the deterministic engine
 */
export interface WeaponData {
  name: string;
  damage_dice: DiceFormula;
  damage_type: DamageType;
  properties: WeaponProperty[];
  range?: WeaponRange;
  is_magical: boolean;
  magic_bonus: number;
  mastery?: WeaponMastery;
  reach_ft: number;
  bonus_damage: BonusDamage[];
  special_effects: WeaponSpecialEffect[];
}

// ============================================================================
// SPELL TYPES
// ============================================================================

export type CastingTime =
  | 'action'
  | 'bonus_action'
  | 'reaction'
  | '1_minute'
  | '10_minutes'
  | '1_hour';

export type SpellRange = { type: 'self' } | { type: 'touch' } | { type: 'feet'; distance: number };

export type SpellTargetType = 'character' | 'point' | 'self';

/**
 * Which characters the FE target-picker menu offers for a spell, from the party
 * POV. Mirrors Rust `SpellTargetAudience`; recomputed by the enricher on load
 * (no ES reindex). FE-menu-only — typed/free-text casts still resolve any
 * target.
 */
export type SpellTargetAudience = 'hostile' | 'friendly' | 'self_only' | 'any';

export type TargetType = 'self' | 'ally' | 'all_allies' | 'enemy' | 'all_enemies' | 'creature';

export type AreaType = 'radius' | 'cone' | 'line' | 'cube' | 'squares';

export type DurationType = 'rounds' | 'minutes' | 'save_ends' | 'until_dispelled';

export type SaveTiming = 'start_of_turn' | 'end_of_turn';

export type BuffType =
  // Numeric bonuses
  | 'ac_bonus'
  | 'ac_set' // Sets AC to fixed value (Mage Armor, Robe of the Archmagi)
  | 'attack_bonus'
  | 'damage_bonus'
  | 'save_bonus'
  | 'spell_attack_bonus' // Bonus to spell attack rolls (Wand of the War Mage)
  | 'spell_dc_bonus' // Bonus to spell save DC
  | 'ignore_cover' // Spell attacks ignore half cover (Wand of the War Mage)
  | 'hp_bonus' // Temporary HP or HP bonus
  | 'skill_bonus' // Bonus to skill checks
  | 'ability_check_bonus' // Bonus to ability checks (Guidance SRD 5.2)

  // Speed modifiers
  | 'speed_bonus' // Flat speed increase (Longstrider +10)
  | 'speed_multiplier'
  | 'flying_speed' // Grants flying speed
  | 'climb_speed' // Grants climbing speed (Spider Climb)
  | 'swim_speed' // Grants swimming speed

  // Resistances/Immunities
  | 'damage_resistance'
  | 'damage_immunity'
  | 'damage_reduction' // Reduces damage by flat/dice (Resistance cantrip)
  | 'condition_immunity' // Immunity to a condition (Heroism: frightened)

  // Advantage/Disadvantage effects
  | 'advantage_on_attacks'
  | 'advantage_on_saves'
  | 'advantage_on_checks' // Advantage on ability checks
  | 'impose_disadvantage_on_attacks' // Attackers have disadvantage (Blur)

  // Visibility effects
  | 'invisible' // Target is invisible
  | 'darkvision' // Grants darkvision (value = range)
  | 'see_invisible' // Can see invisible creatures
  | 'truesight' // Grants truesight (value = range)

  // Ability score effects
  | 'ability_score_set' // Sets ability score to fixed value (Gauntlets of Ogre Power)

  // Weapon transformation effects (Shillelagh, Magic Weapon)
  | 'use_spellcasting_for_melee' // Use spellcasting ability for melee attacks
  | 'weapon_damage_die_override' // Override weapon damage die (Shillelagh)
  | 'weapon_magical' // Weapon attacks count as magical (Magic Weapon)

  // Special effects
  | 'death_ward' // Drop to 1 HP instead of 0, once
  | 'temp_hp_per_turn' // Gain temp HP at start of each turn (Heroism)
  | 'maximize_healing' // Maximize all healing dice received (Beacon of Hope)

  // Utility buff types (track spell is active)
  | 'water_breathing' // Can breathe underwater
  | 'water_walk' // Can walk on liquid surfaces
  | 'slow_fall' // Fall slowly, no fall damage (Feather Fall)
  | 'jump_distance' // Jump distance override (value = distance)
  | 'mirror_images' // Mirror images protecting caster (value = count)
  | 'ethereal' // On the Border Ethereal (Blink, Etherealness)
  | 'sanctuary' // Attackers must save to target
  | 'extra_turns' // Extra turns (Time Stop)
  | 'dash_as_bonus' // Dash as bonus action (Expeditious Retreat)
  | 'levitate' // Hover in place (Levitate)
  | 'transformation' // Polymorphed into another form

  // Wards / immunities
  | 'untargetable' // Cannot be targeted by attacks or spells (Resilient Sphere)
  | 'melee_immune' // Immune to melee attacks (Antilife Shell)
  | 'spell_immunity' // Spells of level ≤ 5 can't affect caster (Globe of Invulnerability)
  | 'max_hp_increase'; // Aid: +5 HP max + current HP per target (+5 per slot above 2)

/**
 * Area shape for AoE spells/effects
 */
export interface Area {
  area_type: AreaType;
  center?: Position;
  size_ft: number;
  squares?: Position[];
}

/**
 * Condition applied by spells/abilities
 */
export interface ConditionApplication {
  condition: Condition;
  duration_type: DurationType;
  duration_value?: number;
  save_dc?: number;
  save_ability?: Ability;
  save_timing?: SaveTiming;
}

/**
 * Secondary effects (e.g., Guiding Bolt grants advantage)
 */
export interface SecondaryEffect {
  condition?: ConditionApplication;
  speed_reduction?: number;
  prevents_healing: boolean;
  reveals_target: boolean;
  /** Forced movement away from caster on failed save (Thunderwave: 10 ft). */
  push_distance_ft?: number;
}

/**
 * Buff effect from spells/abilities
 */
export interface BuffEffect {
  buff_type: BuffType;
  /** Flat numeric value (e.g., +5 AC from Shield) */
  value?: number;
  /**
   * Additional value added per slot level above the spell's base level
   * (Aid: +5 HP max per slot above 2). Resolver computes
   * `value + (slot_level - base_level).max(0) * extra_value_per_level`.
   * Omitted / 0 means upcast has no per-value effect.
   */
  extra_value_per_level?: number;
  /** Dice-based value (e.g., +1d4 from Guidance/Bless) */
  dice?: DiceFormula;
  /** For damage_resistance/immunity/reduction - which damage types */
  damage_types?: DamageType[];
  /** For ability_score_set, advantage_on_checks, advantage_on_saves, ability_check_bonus - which ability */
  ability?: Ability;
  /** For skill_bonus - which skill (e.g., "stealth", "perception") */
  skill?: string;
  /** For condition immunity effects */
  condition?: Condition;
}

/**
 * Area effect trigger (for AreaCreation spells)
 */
export interface AreaEffectTrigger {
  damage?: DiceFormula;
  damage_type?: DamageType;
  save_dc?: number;
  save_ability?: Ability;
  half_on_save: boolean;
  condition_on_fail?: ConditionApplication;
}

// ============================================================================
// SPELL EFFECT VARIANTS (15 types - matches Rust engine exactly)
// ============================================================================

/**
 * Attack roll spells (Fire Bolt, Guiding Bolt, Scorching Ray, Eldritch Blast)
 */
export interface SpellEffectAttackRoll {
  type: 'attack_roll';
  /** Computed at intent time: proficiency + spellcasting mod */
  attack_bonus?: number;
  damage: DiceFormula;
  damage_type: DamageType;
  extra_damage_per_level?: DiceFormula;
  secondary_effect?: SecondaryEffect;
  /** Number of beams/rays (Eldritch Blast, Scorching Ray). Default 1. */
  beams?: number;
  /** Extra beams per cantrip scaling level (Eldritch Blast: 1 extra at 5/11/17) */
  extra_beams_per_level?: number;
}

/**
 * Additional damage of a different type (Flame Strike: fire + radiant)
 */
export interface AdditionalDamage {
  damage: DiceFormula;
  damage_type: DamageType;
}

/**
 * Saving throw spells (Fireball, Hold Person, Web)
 */
export interface SpellEffectSavingThrow {
  type: 'saving_throw';
  ability: Ability;
  /** Computed at intent time: 8 + proficiency + spellcasting mod */
  dc?: number;
  damage?: DiceFormula;
  damage_type?: DamageType;
  half_on_save: boolean;
  extra_damage_per_level?: DiceFormula;
  condition_on_fail?: ConditionApplication;
  area?: Area;
  /** Additional damage of a different type (Flame Strike: fire + radiant) */
  additional_damage?: AdditionalDamage;
  /** Secondary effects on failed save (Vicious Mockery: disadvantage on next attack) */
  secondary_effect?: SecondaryEffect;
}

/**
 * Auto-hit spells (Magic Missile, Cure Wounds, Healing Word)
 */
export interface SpellEffectAutoHit {
  type: 'auto_hit';
  damage?: DiceFormula;
  damage_type?: DamageType;
  healing?: DiceFormula;
  projectiles: number;
  extra_projectiles_per_level: number;
}

/**
 * Area creation spells (Web, Fog Cloud, Wall of Fire)
 */
export interface SpellEffectAreaCreation {
  type: 'area_creation';
  area: Area;
  duration_rounds: number;
  effect_on_enter?: AreaEffectTrigger;
  effect_on_start_turn?: AreaEffectTrigger;
  effect_on_end_turn?: AreaEffectTrigger;
}

/**
 * Buff spells (Shield, Bless, Haste, Invisibility)
 */
export interface SpellEffectBuff {
  type: 'buff';
  targets: TargetType;
  effects: BuffEffect[];
  duration_rounds?: number;
  /** For spells that can only target self (e.g., Invisibility "self only") */
  self_only?: boolean;
  /** Grants an extra action per turn (Haste) */
  extra_action?: boolean;
  /** Causes lethargy when spell ends (Haste) */
  lethargy_on_end?: boolean;
  /** Immunity to Magic Missile spell (Shield) */
  magic_missile_immunity?: boolean;
  /** Target can hover while flying (Fly) */
  can_hover?: boolean;
  /**
   * Maximum number of targets at base level. SRD 5.2 multi-target buffs:
   * - Bless: 3 (+1 per slot level above 1)
   * - Aid: 3 (single-spell upcast adds HP, not targets)
   * - Mass Healing Word: 6
   *
   * `undefined` = single-target buff (Shield, Haste, Invisibility, etc.).
   * When present, the FE picker switches to multi-select up to this count
   * plus `extra_targets_per_level` per upcast slot above base level.
   */
  max_targets?: number;
  /**
   * Extra targets granted per slot level above base. Required (defaults to
   * `0` for single-target buffs). Rust's `SpellEffect::Buff` serializes
   * this field unconditionally — no `skip_serializing_if` — so the wire
   * payload always has it. Keeping the TS type required mirrors that
   * contract and stops the FE from needing `?? 0` guards.
   */
  extra_targets_per_level: number;
}

/**
 * Attack data for summoned entity (Spiritual Weapon)
 */
export interface SummonAttack {
  attack_bonus: number;
  damage: DiceFormula;
  damage_type: DamageType;
}

/**
 * Summon spells (Conjure Animals, Find Familiar, Spiritual Weapon)
 */
export interface SpellEffectSummon {
  type: 'summon';
  /** Creature template ID to summon (for named creatures) */
  creature_id?: string;
  /** Description of summoned entity (for Spiritual Weapon, etc.) */
  description?: string;
  /** Number of creatures summoned */
  count?: number;
  /** Duration in rounds */
  duration_rounds?: number;
  /** Attack data for summoned entity (Spiritual Weapon) */
  attack?: SummonAttack;
}

/**
 * Unknown effect - placeholder for enricher to fill in
 */
export interface SpellEffectUnknown {
  type: 'unknown';
}

/**
 * Debuff spells (Bane, Slow) - penalties without direct damage
 */
export interface SpellEffectDebuff {
  type: 'debuff';
  ability: Ability;
  dc?: number;
  penalty_dice?: DiceFormula;
  speed_reduction?: number;
  speed_halved?: boolean;
  ac_penalty?: number;
  dex_save_penalty?: number;
  limits_actions?: boolean;
  no_reactions?: boolean;
  half_on_save?: boolean;
  save_timing?: SaveTiming;
  area?: Area;
  max_targets?: number;
}

/**
 * Dispel spells (Dispel Magic) - removes magical effects
 */
export interface SpellEffectDispel {
  type: 'dispel';
  /** Base level of spells automatically dispelled (3 for Dispel Magic) */
  auto_success_level: number;
  /** Ability used for check to dispel higher level spells */
  check_ability: Ability;
}

/**
 * Condition removal spells (Lesser/Greater Restoration, Remove Curse)
 */
export interface SpellEffectRemoveCondition {
  type: 'remove_condition';
  /** List of conditions that can be removed */
  conditions: Condition[];
  /** Also removes curses (for Remove Curse) */
  removes_curse?: boolean;
  /** Also removes one exhaustion level (Greater Restoration) */
  removes_exhaustion?: boolean;
}

/**
 * Teleportation spells (Dimension Door, Misty Step, Plane Shift)
 */
export interface SpellEffectTeleport {
  type: 'teleport';
  /** Description of destination */
  destination?: string;
  /** Maximum teleport distance in feet (undefined for unlimited/planar) */
  range_ft?: number;
  /** Can bring willing creatures (passengers count) */
  passengers?: number;
}

/**
 * Utility spells with narrative-only effects (Detect Magic, Light)
 */
export interface SpellEffectUtility {
  type: 'utility';
  /** Description of the effect for roleplay/narrative */
  description: string;
}

/**
 * Transformation spells (Polymorph, True Polymorph)
 */
export interface SpellEffectTransformation {
  type: 'transformation';
  ability: Ability;
  dc?: number;
  target_form: string;
  max_cr?: number;
  duration_rounds?: number;
  unwilling_save?: boolean;
}

/**
 * Wall spells (Wall of Fire, Wall of Force, Wall of Stone)
 */
export interface SpellEffectWall {
  type: 'wall';
  area: Area;
  duration_rounds: number;
  blocks_movement?: boolean;
  blocks_los?: boolean;
  passage_damage?: DiceFormula;
  damage_type?: DamageType;
  save_dc?: number;
  save_ability?: Ability;
  half_on_save?: boolean;
  proximity_damage?: DiceFormula;
}

/**
 * Effect for HP threshold spells (Power Word Kill, Power Word Stun)
 */
export type HpThresholdEffect =
  | { type: 'instant_death' }
  | {
      type: 'apply_condition';
      condition: Condition;
      duration_rounds?: number;
      save_timing?: SaveTiming;
      save_ability?: Ability;
      save_dc?: number;
    }
  | {
      type: 'damage';
      dice: DiceFormula;
      damage_type: DamageType;
    };

/**
 * HP threshold spells (Power Word Kill, Power Word Stun)
 */
export interface SpellEffectHpThreshold {
  type: 'hp_threshold';
  hp_threshold: number;
  effect_below: HpThresholdEffect;
  effect_above?: HpThresholdEffect;
}

/**
 * Phase of a multi-phase spell
 */
export interface SpellPhase {
  round: number;
  effect: SpellEffect;
}

/**
 * Multi-phase spells (Storm of Vengeance) - different effects per round
 */
export interface SpellEffectMultiPhase {
  type: 'multi_phase';
  phases: SpellPhase[];
  duration_rounds: number;
  area: Area;
}

/**
 * Union type for all spell effects
 */
export type SpellEffect =
  | SpellEffectUnknown
  | SpellEffectAttackRoll
  | SpellEffectSavingThrow
  | SpellEffectAutoHit
  | SpellEffectAreaCreation
  | SpellEffectBuff
  | SpellEffectSummon
  | SpellEffectDebuff
  | SpellEffectDispel
  | SpellEffectRemoveCondition
  | SpellEffectTeleport
  | SpellEffectUtility
  | SpellEffectTransformation
  | SpellEffectWall
  | SpellEffectHpThreshold
  | SpellEffectMultiPhase;

/**
 * Complete spell data for the deterministic engine
 */
export interface SpellData {
  name: string;
  base_level: number;
  school: SpellSchool;
  casting_time: CastingTime;
  range: SpellRange;
  concentration: boolean;
  ritual: boolean;
  effect: SpellEffect;
  /** FE target-picker audience — mirrors Rust `SpellData.target_audience`
   *  (re-derived on load; absent on legacy ES docs). Menu-only curation. */
  target_audience?: SpellTargetAudience;
  /** Short player-facing summary for UI tooltips (~15-25 words) */
  summary?: string;
}

// ============================================================================
// ABILITY TYPES (class features, feats, racial abilities)
// ============================================================================

export type DamageTrigger = 'on_hit' | 'always';

export type ExtraActionType = 'action' | 'attack' | 'any';

export type RerollType = 'attack' | 'save' | 'check' | 'any';

/**
 * Resource type enum (matches Rust ResourceType)
 */
export type ResourceType =
  // Barbarian
  | 'rage'
  // Bard
  | 'bardic_inspiration'
  // Cleric/Paladin
  | 'channel_divinity'
  // Druid
  | 'wild_shape'
  // Fighter
  | 'action_surge'
  | 'second_wind'
  | 'superiority_dice'
  | 'indomitable'
  // Monk (Rust also has `focus_points`)
  | 'ki'
  | 'focus_points'
  // Paladin
  | 'lay_on_hands_pool'
  | 'divine_sense'
  // Ranger
  | 'favored_foe'
  | 'natures_veil'
  | 'tireless'
  // Sorcerer
  | 'sorcery_points'
  // Warlock
  | 'eldritch_invocation'
  | 'pact_magic_slots'
  // Wizard
  | 'arcane_recovery'
  // Generic
  | 'spell_slot'
  | 'hit_dice'
  // Feats/Items
  | 'luck'
  | 'charge'
  // Monster
  | 'legendary_action'
  // Species (PB uses per long rest via feature_uses map)
  | 'feature_use';

/**
 * Resource cost for abilities (ki, rage uses, etc.)
 */
export interface ResourceCost {
  resource_type: ResourceType;
  amount: number;
  /** For spell_slot type: which level slot (1-9) */
  spell_slot_level?: number;
}

// ============================================================================
// ABILITY EFFECT VARIANTS (5 types - matches engine exactly)
// ============================================================================

/**
 * Extra damage abilities (Sneak Attack, Divine Smite)
 */
export interface AbilityEffectExtraDamage {
  type: 'extra_damage';
  dice?: DiceFormula;
  flat_bonus?: number;
  damage_type: DamageType;
  trigger: DamageTrigger;
  conditions: string[];
}

/**
 * Self-healing abilities (Second Wind, Lay on Hands)
 */
export interface AbilityEffectSelfHealing {
  type: 'self_healing';
  dice?: DiceFormula;
  flat_bonus?: number;
}

/**
 * Buff abilities (Rage, Bardic Inspiration)
 */
export interface AbilityEffectBuff {
  type: 'buff';
  target: TargetType;
  effects: BuffEffect[];
  duration_rounds?: number;
}

/**
 * Extra action abilities (Action Surge, Haste)
 */
export interface AbilityEffectExtraAction {
  type: 'extra_action';
  action_type: ExtraActionType;
  restrictions: string[];
}

/**
 * Reroll abilities (Lucky, Indomitable)
 */
export interface AbilityEffectReroll {
  type: 'reroll';
  roll_type: RerollType;
  must_use_new: boolean;
}

/**
 * Union type for all ability effects
 */
export type AbilityEffect =
  | AbilityEffectExtraDamage
  | AbilityEffectSelfHealing
  | AbilityEffectBuff
  | AbilityEffectExtraAction
  | AbilityEffectReroll;

/**
 * Complete ability data for the deterministic engine
 */
export interface AbilityData {
  name: string;
  class_name?: string;
  resource_cost?: ResourceCost;
  effect: AbilityEffect;
}

// ============================================================================
// REACTION TYPES
// ============================================================================

/**
 * Conditions for when a reaction triggers
 */
export interface ReactionConditions {
  self_is_target?: boolean;
  target_in_range_ft?: number;
  min_spell_slot?: number;
}

// ============================================================================
// UNARMED STRIKE TYPES (for grapple/shove)
// ============================================================================

export type UnarmedOptionType = 'damage' | 'grapple' | 'shove';

export type ShoveType = 'prone' | 'push';

/**
 * Unarmed strike option (for martial arts, grapple, shove)
 */
export interface UnarmedStrikeOption {
  option_type: UnarmedOptionType;
  target_id: string;
  damage_type: DamageType;
  damage_dice?: DiceFormula;
  is_magical: boolean;
  /** Computed at intent time: 8 + prof + STR mod */
  dc?: number;
  shove_type?: ShoveType;
  push_distance_ft?: number;
}

// ============================================================================
// COMBAT ACTION TYPES
// ============================================================================

/** Wire-format `ActionSlot` — mirrors Rust `ActionSlot` enum with snake_case
 *  serialization. Used both in elasticsearch action-card data and on the
 *  WebSocket `PlayerIntent` envelope. */
export type ActionSlot =
  | 'action'
  | 'bonus_action'
  | 'reaction'
  | 'free'
  | 'movement'
  | 'lair_action'
  | 'legendary_action';

export type AdvantageState = 'normal' | 'advantage' | 'disadvantage';

// ============================================================================
// MAGIC ITEM TYPES
// ============================================================================

export type ItemType =
  | 'weapon'
  | 'armor'
  | 'wondrous'
  | 'potion'
  | 'wand'
  | 'staff'
  | 'ring'
  | 'rod'
  | 'scroll'
  | 'ammunition';

export type ItemActivation = 'action' | 'bonus_action' | 'reaction' | 'none';

/** When a magic item refreshes its charges. Wrapped in {@link Recharge} so the
 *  dice formula (how many charges restore) stays separate from the trigger. */
export type RechargeWhen =
  | 'dawn'
  | 'short_rest'
  | 'long_rest'
  | 'never'
  | { daily: { uses: number } };

/** Magic-item recharge spec: trigger + optional dice formula (e.g.
 *  `{ when: 'dawn', dice: '1d6+4' }`). Authored as a structured object so
 *  the two halves of the SRD value ("1d6+4 at dawn") aren't crammed into
 *  one string. `dice` omitted = full recharge. */
export interface Recharge {
  when: RechargeWhen;
  dice?: string;
}

/**
 * Item effect when used
 */
export type ItemEffect =
  | ItemEffectCastSpell
  | ItemEffectGrantBuff
  | ItemEffectDealDamage
  | ItemEffectHeal
  | ItemEffectApplyCondition
  | ItemEffectSpecial;

export interface ItemEffectCastSpell {
  type: 'cast_spell';
  spell: SpellData;
  fixed_dc?: number;
  fixed_attack_bonus?: number;
}

export interface ItemEffectGrantBuff {
  type: 'grant_buff';
  effects: BuffEffect[];
  duration_rounds?: number;
}

export interface ItemEffectDealDamage {
  type: 'deal_damage';
  damage: DiceFormula;
  damage_type: DamageType;
  save_dc?: number;
  save_ability?: Ability;
  half_on_save: boolean;
  area?: Area;
}

export interface ItemEffectHeal {
  type: 'heal';
  healing: DiceFormula;
}

export interface ItemEffectApplyCondition {
  type: 'apply_condition';
  condition: Condition;
  save_dc: number;
  save_ability: Ability;
  duration_type: DurationType;
  duration_value?: number;
}

export interface ItemEffectSpecial {
  type: 'special';
  description: string;
}

/**
 * Magic item with combat-relevant data
 */
export interface MagicItemData {
  name: string;
  item_type: ItemType;
  activation: ItemActivation;
  charges?: number;
  max_charges?: number;
  recharge?: Recharge;
  effect: ItemEffect;
  attunement_required: boolean;
}

// ============================================================================
// ENHANCED ELASTICSEARCH DOCUMENT TYPES
// ============================================================================

/**
 * Enhanced spell document with typed mechanics
 * Maps directly to Rust SpellData for deterministic engine
 *
 * Note: 'name' comes from top-level document, not stored in mechanics
 */
export interface SpellMechanics {
  /** Spell level (0-9, 0 = cantrip) */
  base_level: number;
  /** Spell school enum */
  school: SpellSchool;
  /** Casting time enum */
  casting_time: CastingTime;
  /** Range enum */
  range: SpellRange;
  /** Whether spell requires concentration */
  concentration: boolean;
  /** Whether spell can be cast as a ritual */
  ritual: boolean;
  /** The typed spell effect for the deterministic engine */
  effect: SpellEffect;
  /** FE target-picker audience — mirrors Rust `SpellData.target_audience`. */
  target_audience?: SpellTargetAudience;
}

/**
 * Enhanced weapon document with typed mechanics
 */
export interface WeaponMechanics {
  /** Parsed damage dice */
  damage_dice: DiceFormula;
  /** Parsed damage type */
  damage_type_enum: DamageType;
  /** Parsed properties */
  properties_enum: WeaponProperty[];
  /** Weapon mastery (2024 rules) */
  mastery?: WeaponMastery;
  /** Reach in feet (5 or 10) */
  reach_ft: number;
  /** Whether this weapon is magical */
  is_magical: boolean;
  /** Magic bonus (+1, +2, +3) */
  magic_bonus: number;
}

/**
 * Class name enum (matches Rust ClassName)
 */
export type ClassName =
  | 'barbarian'
  | 'bard'
  | 'cleric'
  | 'druid'
  | 'fighter'
  | 'monk'
  | 'paladin'
  | 'ranger'
  | 'rogue'
  | 'sorcerer'
  | 'warlock'
  | 'wizard';

/**
 * Prepared casters can change spells on long rest.
 * Matches Rust `is_prepared_caster_class()` in adventure_prep/mod.rs.
 */
export const PREPARED_CASTER_CLASSES: ReadonlySet<ClassName> = new Set([
  'wizard',
  'cleric',
  'druid',
  'paladin',
  'ranger',
]);

/**
 * Known casters have fixed spells — no preparation needed.
 * Matches Rust `is_known_caster_class()` in adventure_prep/mod.rs.
 */
export const KNOWN_CASTER_CLASSES: ReadonlySet<ClassName> = new Set([
  'bard',
  'sorcerer',
  'warlock',
]);

/**
 * Enhanced class feature document with typed mechanics
 * Maps directly to Rust AbilityData for deterministic engine
 *
 * Note: 'name' comes from top-level document, not stored in mechanics
 */
export interface AbilityMechanics {
  /** Class that grants this ability (optional for feats/racial abilities) */
  class_name?: ClassName;
  /** Resource cost if any */
  resource_cost?: ResourceCost;
  /** The typed ability effect for the deterministic engine */
  effect: AbilityEffect;
}

// ============================================================================
// SCALING PATTERNS (for index storage)
// ============================================================================

/**
 * Scaling information for abilities that scale with level
 * (e.g., Sneak Attack 1d6 per 2 levels)
 *
 * Stored in index, LLM calculates final dice at intent time
 */
export interface DiceScaling {
  base_dice: DiceFormula;
  per_levels: number;
  max_dice?: number;
}

/**
 * Cantrip damage scaling (5th, 11th, 17th level)
 */
export interface CantripScaling {
  level_5_dice: DiceFormula;
  level_11_dice: DiceFormula;
  level_17_dice: DiceFormula;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isSpellEffectAttackRoll(effect: SpellEffect): effect is SpellEffectAttackRoll {
  return effect.type === 'attack_roll';
}

export function isSpellEffectSavingThrow(effect: SpellEffect): effect is SpellEffectSavingThrow {
  return effect.type === 'saving_throw';
}

export function isSpellEffectAutoHit(effect: SpellEffect): effect is SpellEffectAutoHit {
  return effect.type === 'auto_hit';
}

export function isSpellEffectAreaCreation(effect: SpellEffect): effect is SpellEffectAreaCreation {
  return effect.type === 'area_creation';
}

export function isSpellEffectBuff(effect: SpellEffect): effect is SpellEffectBuff {
  return effect.type === 'buff';
}

export function isSpellEffectSummon(effect: SpellEffect): effect is SpellEffectSummon {
  return effect.type === 'summon';
}

export function isSpellEffectUnknown(effect: SpellEffect): effect is SpellEffectUnknown {
  return effect.type === 'unknown';
}

export function isSpellEffectDebuff(effect: SpellEffect): effect is SpellEffectDebuff {
  return effect.type === 'debuff';
}

export function isSpellEffectDispel(effect: SpellEffect): effect is SpellEffectDispel {
  return effect.type === 'dispel';
}

export function isSpellEffectRemoveCondition(
  effect: SpellEffect
): effect is SpellEffectRemoveCondition {
  return effect.type === 'remove_condition';
}

export function isSpellEffectTeleport(effect: SpellEffect): effect is SpellEffectTeleport {
  return effect.type === 'teleport';
}

export function isSpellEffectUtility(effect: SpellEffect): effect is SpellEffectUtility {
  return effect.type === 'utility';
}

export function isSpellEffectTransformation(
  effect: SpellEffect
): effect is SpellEffectTransformation {
  return effect.type === 'transformation';
}

export function isSpellEffectWall(effect: SpellEffect): effect is SpellEffectWall {
  return effect.type === 'wall';
}

export function isSpellEffectHpThreshold(effect: SpellEffect): effect is SpellEffectHpThreshold {
  return effect.type === 'hp_threshold';
}

export function isSpellEffectMultiPhase(effect: SpellEffect): effect is SpellEffectMultiPhase {
  return effect.type === 'multi_phase';
}

export function isAbilityEffectExtraDamage(
  effect: AbilityEffect
): effect is AbilityEffectExtraDamage {
  return effect.type === 'extra_damage';
}

export function isAbilityEffectSelfHealing(
  effect: AbilityEffect
): effect is AbilityEffectSelfHealing {
  return effect.type === 'self_healing';
}

export function isAbilityEffectBuff(effect: AbilityEffect): effect is AbilityEffectBuff {
  return effect.type === 'buff';
}

export function isAbilityEffectExtraAction(
  effect: AbilityEffect
): effect is AbilityEffectExtraAction {
  return effect.type === 'extra_action';
}

export function isAbilityEffectReroll(effect: AbilityEffect): effect is AbilityEffectReroll {
  return effect.type === 'reroll';
}

// ============================================================================
// MONSTER COMBAT MECHANICS
// ============================================================================

/**
 * Pre-computed stats for fast access during combat
 */
export interface MonsterComputedStats {
  // Ability modifiers
  str_mod: number;
  dex_mod: number;
  con_mod: number;
  int_mod: number;
  wis_mod: number;
  cha_mod: number;

  // Save modifiers (includes proficiency if applicable)
  saves: {
    str: number;
    dex: number;
    con: number;
    int: number;
    wis: number;
    cha: number;
  };

  // Derived stats
  initiative_bonus: number;
  passive_perception: number;

  // Skill bonuses (total modifier including proficiency/expertise)
  skills?: Record<string, number>;

  // Spellcasting (if applicable)
  spell_save_dc?: number;
  spell_attack_bonus?: number;
}

/**
 * Monster attack action with full mechanics
 */
export interface MonsterAttackMechanics {
  name: string;
  attack_type: 'melee_weapon' | 'ranged_weapon' | 'melee_spell' | 'ranged_spell';
  attack_bonus: number;
  reach_ft?: number;
  range?: WeaponRange;
  damage: DiceFormula;
  damage_type: DamageType;
  is_magical: boolean;

  // Bonus damage (poison, elemental, etc.)
  bonus_damage?: { dice: DiceFormula; damage_type: DamageType }[];

  // Secondary effects on hit
  on_hit?: {
    // Condition applied on hit (e.g., Poisoned, Paralyzed)
    condition?: {
      type: Condition;
      save?: { ability: Ability; dc: number };
      duration_type?: DurationType;
      duration_value?: number;
    };
    // Extra damage with save (e.g., poison damage)
    extra_damage?: {
      dice: DiceFormula;
      damage_type: DamageType;
      save?: { ability: Ability; dc: number; half_on_save: boolean };
    };
    // Grapple on hit
    grapple?: { escape_dc: number; size_limit?: string };
    // Push/prone on hit
    forced_movement?: {
      type: 'push' | 'prone';
      distance_ft?: number;
      save?: { ability: Ability; dc: number };
    };
  };
}

/**
 * Multiattack configuration
 */
export interface MonsterMultiattack {
  description: string;
  // Standard combo: ["Bite", "Claw", "Claw"]
  attacks: string[];
  // Can replace attacks with abilities (e.g., "Can replace one with Fire Breath")
  replacements?: { attack: string; with_ability: string }[];
}

/**
 * Monster spellcasting with FULL embedded SpellData
 */
export interface MonsterSpellcasting {
  ability: Ability;
  spell_save_dc: number;
  spell_attack_bonus: number;

  // FULL SpellData embedded for each spell (not references!)
  at_will: SpellData[];
  per_day: { uses: number; spells: SpellData[] }[];
  // For monsters with spell slots (rare)
  spell_slots?: { level: number; slots: number; spells: SpellData[] }[];
}

/**
 * Monster special ability with full mechanics
 */
export interface MonsterAbilityMechanics {
  name: string;
  ability_type: 'trait' | 'action' | 'bonus_action' | 'reaction' | 'legendary' | 'lair';

  // Resource cost (for recharge, per-day, legendary actions)
  resource_cost?: {
    type: 'legendary_action' | 'recharge' | 'per_day' | 'per_short_rest' | 'per_long_rest';
    amount?: number; // Legendary action cost
    recharge?: string; // "5-6", "6"
    uses?: number; // 3/day
  };

  // The effect (reuses existing AbilityEffect types where possible)
  effect: MonsterAbilityEffect;
}

/**
 * Monster ability effect types
 */
export type MonsterAbilityEffect =
  | MonsterEffectDamage
  | MonsterEffectSave
  | MonsterEffectBuff
  | MonsterEffectHealing
  | MonsterEffectSpecial;

export interface MonsterEffectDamage {
  type: 'damage';
  damage: DiceFormula;
  damage_type: DamageType;
  area?: Area;
  save?: { ability: Ability; dc: number; half_on_save: boolean };
}

export interface MonsterEffectSave {
  type: 'save';
  ability: Ability;
  dc: number;
  on_fail: {
    damage?: { dice: DiceFormula; damage_type: DamageType };
    condition?: Condition;
    duration_type?: DurationType;
    duration_value?: number;
  };
  on_success?: {
    damage?: { dice: DiceFormula; damage_type: DamageType }; // Half damage
  };
  area?: Area;
}

export interface MonsterEffectBuff {
  type: 'buff';
  target: TargetType;
  effects: BuffEffect[];
  duration_rounds?: number;
}

export interface MonsterEffectHealing {
  type: 'healing';
  healing: DiceFormula;
  target: TargetType;
}

export interface MonsterEffectSpecial {
  type: 'special';
  description: string;
  // Structured data for common special effects
  legendary_resistance?: { uses: number };
  magic_resistance?: boolean;
  regeneration?: { amount: number; prevented_by?: DamageType[] };
  frightful_presence?: { range_ft: number; dc: number };
}

/**
 * Monster reaction with trigger and effect
 */
export interface MonsterReactionMechanics {
  name: string;
  trigger: string; // "when hit by melee attack", "when ally within 5ft is attacked"
  effect: MonsterAbilityEffect;
}

/**
 * Legendary action
 */
export interface MonsterLegendaryAction {
  name: string;
  cost: number; // 1, 2, or 3 legendary actions
  effect: MonsterAbilityEffect;
}

/**
 * Complete monster combat mechanics - embedded in monster document
 */
export interface MonsterCombatMechanics {
  // Pre-computed modifiers for fast access
  computed: MonsterComputedStats;

  // Multiattack configuration
  multiattack?: MonsterMultiattack;

  // Individual attack actions with full mechanics
  attacks: MonsterAttackMechanics[];

  // Spellcasting with FULL embedded SpellData
  spellcasting?: MonsterSpellcasting;

  // Special abilities (traits, actions, bonus actions)
  abilities: MonsterAbilityMechanics[];

  // Reactions
  reactions?: MonsterReactionMechanics[];

  // Legendary actions (if any)
  legendary_actions?: MonsterLegendaryAction[];
  legendary_action_count?: number; // How many per round (usually 3)

  // Lair actions (if any)
  lair_actions?: MonsterAbilityMechanics[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse a dice string (e.g., "2d6+3") into a DiceFormula
 */
export function parseDiceString(dice: string): DiceFormula | null {
  const match = dice.match(/^(\d+)d(\d+)([+-]\d+)?$/i);
  if (!match) return null;

  return {
    count: parseInt(match[1], 10),
    sides: parseInt(match[2], 10),
    modifier: match[3] ? parseInt(match[3], 10) : 0,
  };
}

/**
 * Format a DiceFormula as a string (e.g., "2d6+3")
 */
export function formatDiceFormula(formula: DiceFormula): string {
  const base = `${formula.count}d${formula.sides}`;
  if (formula.modifier === 0) return base;
  if (formula.modifier > 0) return `${base}+${formula.modifier}`;
  return `${base}${formula.modifier}`;
}

/**
 * Parse a range string (e.g., "60 feet", "Self", "Touch")
 */
export function parseSpellRange(range: string): SpellRange {
  const lower = range.toLowerCase().trim();

  if (lower === 'self' || lower.startsWith('self')) {
    return { type: 'self' };
  }

  if (lower === 'touch') {
    return { type: 'touch' };
  }

  const feetMatch = range.match(/(\d+)\s*(?:feet|ft\.?)/i);
  if (feetMatch) {
    return { type: 'feet', distance: parseInt(feetMatch[1], 10) };
  }

  // Default to touch for unknown ranges
  return { type: 'touch' };
}

/**
 * Parse a casting time string into enum
 */
export function parseCastingTime(time: string): CastingTime {
  const lower = time.toLowerCase();

  if (lower.includes('bonus')) return 'bonus_action';
  if (lower.includes('reaction')) return 'reaction';
  return 'action';
}
